import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Trash2 } from 'lucide-react';
import { MyContext } from '../../../context';
import { COMMERCE_EVENT_TYPES, COMMERCE_EVENT_DESCRIPTIONS, humanizeEventType } from '../../../utils/commerceEventTypes';
import { getWorkflow, createWorkflow, updateWorkflow, createWorkflowDraft, publishWorkflow } from '../../../utils/queries';
import HelpModalButton from '../../../components/HelpModalButton';
import {
	WORKFLOW_FIELD_REFERENCE,
	WORKFLOW_FIELD_AVAILABILITY_NOTE,
	WORKFLOW_OPERATORS,
	WORKFLOW_ACTION_REFERENCE,
} from '../../../utils/workflowReference';

// Only real domain events make sense as a trigger -- workflow.*/approval.*
// are the engine's own lifecycle events (see pi_ecommerce_api's
// commerceEventTypes.ts), not something an admin should pick as a starting
// point.
const TRIGGER_OPTIONS = COMMERCE_EVENT_TYPES.filter((t) => !t.startsWith('workflow.') && !t.startsWith('approval.'));
const OPERATORS = ['=', '!=', '>', '>=', '<', '<=', 'contains', 'not_contains', 'in', 'not_in', 'is_null', 'is_not_null'];
const ACTION_TYPES = ['hold_order', 'approve_order', 'reject_order', 'send_email', 'notify_role', 'send_to_erp', 'call_webhook', 'check_order_status'];

const emptyForm = {
	name: '',
	description: '',
	triggerEventType: TRIGGER_OPTIONS[0],
	combinator: 'AND',
	leaves: [], // [{field, operator, value}]
	steps: [], // see step shapes below
};

// Config fields the form pre-selects visually (e.g. notify_role's
// "recipientMode" dropdown defaulting to "Store admins") must also be
// written into the step's actual config on creation/action-change -- a
// <select> only fires onChange when the user picks something different from
// its current value, so a default shown purely via `value={config.x || fallback}`
// never lands in state until touched. Submitting with a still-missing
// required field then fails the action's own config validator server-side.
const DEFAULT_ACTION_CONFIG = {
	notify_role: { recipientMode: 'ROLE_ADMIN' },
};
const defaultConfigForAction = (action) => ({ ...(DEFAULT_ACTION_CONFIG[action] || {}) });

// Steps have no user-facing id at all -- a step IS its position in this
// list (1-based, always exactly the order shown on screen). "Then go to
// step" fields store that position as a string and are picked from a
// dropdown (StepRefSelect) built from the current list, so they're always
// valid options rather than free-typed text a user has to keep in sync by
// hand. removeStep (below) re-numbers every reference when a step is
// deleted, so this stays correct automatically.
const emptyActionStep = () => ({ type: 'action', action: 'send_email', config: defaultConfigForAction('send_email'), next: '' });
const emptyWaitStep = () => ({ type: 'wait', mode: 'timer', durationSeconds: 3600, eventType: '', correlationField: '', next: '' });
const emptyApprovalStep = () => ({ type: 'approval', approverRole: 'ADMIN', expiresInSeconds: 604800, onApprove: '', onReject: '' });
const emptyBranchStep = () => ({ type: 'branch', field: '', operator: '=', value: '', trueNext: '', next: '' });

// No "complete" step type here -- it's not required as a workflow's last
// step. Leaving any "then go to"/"if approved"/"if rejected" selection on
// "— End workflow —" (an empty next) already ends the workflow the same
// way; the engine only ever completes because a next-step reference was
// empty or unresolved, never because it saw a dedicated complete step. The
// backend still accepts an explicit `{type: 'complete'}` step (and any
// workflow saved before this change may still have one), so it's rendered
// read-only below if present, just no longer offered as something new to add.
const STEP_FACTORIES = { action: emptyActionStep, wait: emptyWaitStep, approval: emptyApprovalStep, branch: emptyBranchStep };

// Short label for a step's dropdown option / reference target, e.g. "Step 2: Wait".
const describeStep = (step) => {
	if (step.type === 'action') return `Action: ${step.action.replace(/_/g, ' ')}`;
	if (step.type === 'wait') return 'Wait';
	if (step.type === 'approval') return 'Request approval';
	if (step.type === 'branch') return 'Branch';
	return 'Complete workflow';
};

// The backend's branch step takes a `branches: [{condition, next}]` array;
// this builder only exposes a single condition per branch (spec section 39's
// "limited nesting" allowance), so we fold to/from that shape at the edges.
// Step ids sent to the backend are just each step's 1-based position --
// always assigned fresh at save time, so they always match what's on screen.
const toBackendSteps = (steps) =>
	steps.map((step, index) => {
		const id = String(index + 1);
		if (step.type === 'branch') {
			return {
				id,
				type: 'branch',
				branches: step.field ? [{ condition: { field: step.field, operator: step.operator, value: coerceValue(step.value) }, next: step.trueNext || null }] : [],
				next: step.next || null,
			};
		}
		if (step.type === 'action') return { id, type: 'action', action: step.action, config: step.config || {}, next: step.next || null };
		if (step.type === 'wait') {
			return step.mode === 'timer'
				? { id, type: 'wait', mode: 'timer', durationSeconds: Number(step.durationSeconds) || 0, next: step.next || null }
				: { id, type: 'wait', mode: 'event', eventType: step.eventType, correlationField: step.correlationField || undefined, next: step.next || null };
		}
		if (step.type === 'approval') {
			return { id, type: 'approval', approverRole: step.approverRole, expiresInSeconds: Number(step.expiresInSeconds) || undefined, onApprove: step.onApprove || null, onReject: step.onReject || null };
		}
		return { id, type: 'complete' };
	});

// Loading an existing workflow: its saved step ids may not be simple
// positions (workflows created before step ids became position-based, or
// created directly via the API with descriptive ids). Rebuild a map from
// whatever id each step actually has to its current position, and rewrite
// every next/onApprove/onReject/branch-next reference through it, so the
// dropdowns below always show a valid, matching selection either way.
const fromBackendSteps = (steps) => {
	const list = steps || [];
	const positionByOldId = new Map(list.map((s, i) => [s.id, String(i + 1)]));
	const remap = (ref) => (ref ? positionByOldId.get(ref) || '' : '');

	return list.map((step) => {
		if (step.type === 'branch') {
			const first = (step.branches || [])[0];
			return { type: 'branch', field: first?.condition?.field || '', operator: first?.condition?.operator || '=', value: first?.condition?.value ?? '', trueNext: remap(first?.next), next: remap(step.next) };
		}
		if (step.type === 'action') return { type: 'action', action: step.action, config: step.config || {}, next: remap(step.next) };
		if (step.type === 'wait') {
			return step.mode === 'timer'
				? { type: 'wait', mode: 'timer', durationSeconds: step.durationSeconds ?? 3600, eventType: '', correlationField: '', next: remap(step.next) }
				: { type: 'wait', mode: 'event', eventType: step.eventType || '', correlationField: step.correlationField || '', durationSeconds: 3600, next: remap(step.next) };
		}
		if (step.type === 'approval') return { type: 'approval', approverRole: step.approverRole || 'ADMIN', expiresInSeconds: step.expiresInSeconds ?? 604800, onApprove: remap(step.onApprove), onReject: remap(step.onReject) };
		return { type: 'complete' };
	});
};

const coerceValue = (raw) => {
	if (raw === '' || raw == null) return raw;
	if (raw === 'true') return true;
	if (raw === 'false') return false;
	const num = Number(raw);
	return Number.isFinite(num) && String(num) === String(raw).trim() ? num : raw;
};

function FieldReferenceList() {
	return (
		<div className='space-y-5 text-sm'>
			<p className='text-xs text-gray-500'>{WORKFLOW_FIELD_AVAILABILITY_NOTE}</p>
			{WORKFLOW_FIELD_REFERENCE.map((group) => (
				<div key={group.group}>
					<h3 className='font-medium mb-1'>{group.group}</h3>
					<div className='space-y-1'>
						{group.fields.map((field) => (
							<div key={field.path} className='flex gap-2'>
								<code className='text-xs bg-gray-100 rounded px-1.5 py-0.5 shrink-0 h-fit'>{field.path}</code>
								<span className='text-gray-600'>{field.description}</span>
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	);
}

function FieldReferenceContent() {
	return (
		<div className='max-h-[70vh] overflow-y-auto space-y-5 text-sm'>
			<FieldReferenceList />
			<div>
				<h3 className='font-medium mb-1'>Operators</h3>
				<div className='space-y-1'>
					{WORKFLOW_OPERATORS.map((operator) => (
						<div key={operator.op} className='flex gap-2'>
							<code className='text-xs bg-gray-100 rounded px-1.5 py-0.5 shrink-0 h-fit'>{operator.op}</code>
							<span className='text-gray-600'>{operator.description}</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function ActionReferenceContent() {
	return (
		<div className='max-h-[70vh] overflow-y-auto space-y-5 text-sm'>
			{WORKFLOW_ACTION_REFERENCE.map((action) => (
				<div key={action.type}>
					<h3 className='font-medium'>
						{action.label} <code className='text-xs bg-gray-100 rounded px-1.5 py-0.5 ml-1'>{action.type}</code>
					</h3>
					<p className='text-gray-600 mt-0.5'>{action.description}</p>
					{action.config && (
						<div className='mt-1 space-y-1 ml-2'>
							{action.config.map((field) => (
								<div key={field.key} className='flex gap-2'>
									<code className='text-xs bg-gray-100 rounded px-1.5 py-0.5 shrink-0 h-fit'>{field.key}</code>
									<span className='text-gray-600'>{field.description}</span>
								</div>
							))}
						</div>
					)}
				</div>
			))}
			<div>
				<h3 className='font-medium mb-1'>Template variables</h3>
				<p className='text-gray-600 mb-2'>
					Any "Subject"/"Message" field, and the "To" field on Send Email, accept <code className='text-xs bg-gray-100 rounded px-1.5 py-0.5'>{'{{field.path}}'}</code>{' '}
					placeholders using the same fields available to conditions:
				</p>
				<FieldReferenceList />
			</div>
		</div>
	);
}

function TriggerReferenceContent() {
	return (
		<div className='max-h-[70vh] overflow-y-auto space-y-2 text-sm'>
			<p className='text-xs text-gray-500 mb-3'>Every event this platform can trigger a workflow from.</p>
			{TRIGGER_OPTIONS.map((eventType) => (
				<div key={eventType} className='flex gap-2'>
					<code className='text-xs bg-gray-100 rounded px-1.5 py-0.5 shrink-0 h-fit'>{eventType}</code>
					<span className='text-gray-600'>{COMMERCE_EVENT_DESCRIPTIONS[eventType] || humanizeEventType(eventType)}</span>
				</div>
			))}
		</div>
	);
}

function ActionConfigFields({ step, onChange }) {
	const setConfig = (patch) => onChange({ ...step, config: { ...step.config, ...patch } });
	if (step.action === 'reject_order') {
		return (
			<div>
				<label>Reason</label>
				<input className='form-input' value={step.config.reason || ''} onChange={(e) => setConfig({ reason: e.target.value })} placeholder='Rejected by workflow.' />
			</div>
		);
	}
	if (step.action === 'send_email') {
		return (
			<div className='space-y-2 bg-gray-100 p-4'>
				<div>
					<label>To (context field or literal email)</label>
					<input className='form-input' value={step.config.to || ''} onChange={(e) => setConfig({ to: e.target.value })} placeholder='customer.email' />
				</div>
				<div>
					<label>Subject</label>
					<input className='form-input' value={step.config.subject || ''} onChange={(e) => setConfig({ subject: e.target.value })} placeholder='Your order {{order.order_number}} requires approval' />
				</div>
				<div>
					<label>Message</label>
					<textarea className='form-inputx w-full' rows={4} value={step.config.message || ''} onChange={(e) => setConfig({ message: e.target.value })} />
				</div>
			</div>
		);
	}
	if (step.action === 'notify_role') {
		return (
			<div className='space-y-2 bg-gray-100 p-4'>
				<div>
					<label>Notify</label>
					<select className='form-input' value={step.config.recipientMode || 'ROLE_ADMIN'} onChange={(e) => setConfig({ recipientMode: e.target.value })}>
						<option value='ROLE_ADMIN'>Store admins</option>
						<option value='APPROVER_FLAG'>Designated approvers</option>
						<option value='EMAIL_LIST'>Specific emails</option>
					</select>
				</div>
				{step.config.recipientMode === 'EMAIL_LIST' && (
					<div>
						<label>Emails (comma-separated)</label>
						<input className='form-input' value={step.config.recipientEmails || ''} onChange={(e) => setConfig({ recipientEmails: e.target.value })} />
					</div>
				)}
				<div>
					<label>Subject</label>
					<input className='form-input' value={step.config.subject || ''} onChange={(e) => setConfig({ subject: e.target.value })} />
				</div>
				<div>
					<label>Message</label>
					<textarea className='form-inputx w-full' rows={4} value={step.config.message || ''} onChange={(e) => setConfig({ message: e.target.value })} />
				</div>
			</div>
		);
	}
	if (step.action === 'call_webhook') {
		return (
			<div className='space-y-2 bg-gray-100 p-4'>
				<div>
					<label>URL</label>
					<input className='form-input' value={step.config.url || ''} onChange={(e) => setConfig({ url: e.target.value })} placeholder='https://example.com/hooks/workflow' />
				</div>
				<div>
					<label>Signing secret (optional)</label>
					<input className='form-input' value={step.config.secret || ''} onChange={(e) => setConfig({ secret: e.target.value })} />
				</div>
			</div>
		);
	}
	return <p className='text-xs text-gray-400'>No configuration needed for this action.</p>;
}

// A "then go to step" field, rendered as a dropdown of every OTHER step's
// current position + short description (e.g. "Step 3: Wait") instead of a
// free-typed id -- always shows valid, up-to-date targets since it's built
// from the live steps array on every render.
function StepRefSelect({ steps, currentIndex, value, onChange, endLabel = 'End workflow' }) {
	return (
		<select className='form-input' value={value || ''} onChange={(e) => onChange(e.target.value)}>
			<option value=''>{`— ${endLabel} —`}</option>
			{steps.map((step, i) =>
				i === currentIndex ? null : (
					<option key={i} value={String(i + 1)}>
						{`Step ${i + 1}: ${describeStep(step)}`}
					</option>
				),
			)}
		</select>
	);
}

function StepCard({ step, index, steps, onChange, onRemove }) {
	return (
		<div className='border border-gray-200 rounded-lg p-4 space-y-3'>
			<div className='flex items-center justify-between'>
				<span className='inline-flex items-center gap-2 text-sm font-medium'>
					<span className='inline-flex items-center justify-center size-6 rounded-full bg-black text-white text-xs font-semibold shrink-0'>{index + 1}</span>
					Step {index + 1}
				</span>
				<button type='button' className='text-gray-400 hover:text-red-600' onClick={onRemove}>
					<Trash2 className='size-4' />
				</button>
			</div>

			<div>
				<label>Type</label>
				<select className='form-input' value={step.type} onChange={(e) => onChange(STEP_FACTORIES[e.target.value]())}>
					<option value='action'>Run an action</option>
					<option value='wait'>Wait</option>
					<option value='approval'>Request approval</option>
					<option value='branch'>Branch</option>
					{step.type === 'complete' && <option value='complete'>Complete workflow (legacy)</option>}
				</select>
			</div>

			{step.type === 'action' && (
				<>
					<div>
						<label>Action</label>
						<select className='form-input' value={step.action} onChange={(e) => onChange({ ...step, action: e.target.value, config: defaultConfigForAction(e.target.value) })}>
							{ACTION_TYPES.map((a) => (
								<option key={a} value={a}>
									{a.replace(/_/g, ' ')}
								</option>
							))}
						</select>
					</div>
					<ActionConfigFields step={step} onChange={onChange} />
					<div>
						<label>Then go to</label>
						<StepRefSelect steps={steps} currentIndex={index} value={step.next} onChange={(next) => onChange({ ...step, next })} />
					</div>
				</>
			)}

			{step.type === 'wait' && (
				<>
					<div>
						<label>Wait for</label>
						<select className='form-input' value={step.mode} onChange={(e) => onChange({ ...step, mode: e.target.value })}>
							<option value='timer'>A fixed amount of time</option>
							<option value='event'>Another event</option>
						</select>
					</div>
					{step.mode === 'timer' ? (
						<div>
							<label>Duration (seconds)</label>
							<input type='number' className='form-input' value={step.durationSeconds} onChange={(e) => onChange({ ...step, durationSeconds: e.target.value })} />
						</div>
					) : (
						<div className='grid grid-cols-2 gap-3'>
							<div>
								<label>Event type</label>
								<select className='form-input' value={step.eventType} onChange={(e) => onChange({ ...step, eventType: e.target.value })}>
									<option value=''>Select...</option>
									{COMMERCE_EVENT_TYPES.map((t) => (
										<option key={t} value={t}>
											{humanizeEventType(t)}
										</option>
									))}
								</select>
							</div>
							<div>
								<label>Correlation field</label>
								<input className='form-input' value={step.correlationField} onChange={(e) => onChange({ ...step, correlationField: e.target.value })} placeholder='order.id' />
							</div>
						</div>
					)}
					<div>
						<label>Then go to</label>
						<StepRefSelect steps={steps} currentIndex={index} value={step.next} onChange={(next) => onChange({ ...step, next })} />
					</div>
				</>
			)}

			{step.type === 'approval' && (
				<>
					<div className='grid grid-cols-2 gap-3'>
						<div>
							<label>Approver</label>
							<select className='form-input' value={step.approverRole} onChange={(e) => onChange({ ...step, approverRole: e.target.value })}>
								<option value='ADMIN'>Store admins</option>
								<option value='PLATFORMADMIN'>Platform admins</option>
							</select>
						</div>
						<div>
							<label>Expires after (seconds)</label>
							<input type='number' className='form-input' value={step.expiresInSeconds} onChange={(e) => onChange({ ...step, expiresInSeconds: e.target.value })} />
						</div>
					</div>
					<div className='grid grid-cols-2 gap-3'>
						<div>
							<label>If approved, go to</label>
							<StepRefSelect steps={steps} currentIndex={index} value={step.onApprove} onChange={(onApprove) => onChange({ ...step, onApprove })} />
						</div>
						<div>
							<label>If rejected, go to</label>
							<StepRefSelect steps={steps} currentIndex={index} value={step.onReject} onChange={(onReject) => onChange({ ...step, onReject })} />
						</div>
					</div>
				</>
			)}

			{step.type === 'branch' && (
				<>
					<p className='text-xs text-gray-400'>If this condition is true, go to one step; otherwise go to another.</p>
					<div className='grid grid-cols-3 gap-3'>
						<input className='form-input' value={step.field} onChange={(e) => onChange({ ...step, field: e.target.value })} placeholder='order.total' />
						<select className='form-input' value={step.operator} onChange={(e) => onChange({ ...step, operator: e.target.value })}>
							{OPERATORS.map((op) => (
								<option key={op} value={op}>
									{op}
								</option>
							))}
						</select>
						<input className='form-input' value={step.value} onChange={(e) => onChange({ ...step, value: e.target.value })} placeholder='value' />
					</div>
					<div className='grid grid-cols-2 gap-3'>
						<div>
							<label>If true, go to</label>
							<StepRefSelect steps={steps} currentIndex={index} value={step.trueNext} onChange={(trueNext) => onChange({ ...step, trueNext })} />
						</div>
						<div>
							<label>Otherwise, go to</label>
							<StepRefSelect steps={steps} currentIndex={index} value={step.next} onChange={(next) => onChange({ ...step, next })} />
						</div>
					</div>
				</>
			)}

			{step.type === 'complete' && <p className='text-xs text-gray-400'>Ends the workflow successfully.</p>}
		</div>
	);
}

export default function WorkflowEditor() {
	const { id } = useParams();
	const navigate = useNavigate();
	const isNew = !id;
	const { activeStoreId } = useContext(MyContext);

	const [form, setForm] = useState(emptyForm);
	const [meta, setMeta] = useState(null); // { status, current_version, editable_version }
	const [loading, setLoading] = useState(!isNew);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (isNew) return;
		setLoading(true);
		getWorkflow(id, { storeId: activeStoreId }).then((result) => {
			if (!result?.success) {
				setLoading(false);
				return;
			}
			const workflow = result.data;
			setMeta(workflow);
			const definition = workflow.editable_version?.definition || {};
			const conditions = definition.conditions;
			setForm({
				name: workflow.name,
				description: workflow.description || '',
				triggerEventType: definition.trigger?.eventType || TRIGGER_OPTIONS[0],
				combinator: conditions?.operator || 'AND',
				leaves: (conditions?.conditions || conditions ? conditions.conditions || [conditions] : []).map((c) => ({ field: c.field, operator: c.operator, value: c.value ?? '' })),
				steps: fromBackendSteps(definition.steps),
			});
			setLoading(false);
		});
	}, [id, isNew, activeStoreId]);

	const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

	const addLeaf = () => setForm((prev) => ({ ...prev, leaves: [...prev.leaves, { field: '', operator: '=', value: '' }] }));
	const updateLeaf = (index, patch) =>
		setForm((prev) => ({ ...prev, leaves: prev.leaves.map((l, i) => (i === index ? { ...l, ...patch } : l)) }));
	const removeLeaf = (index) => setForm((prev) => ({ ...prev, leaves: prev.leaves.filter((_, i) => i !== index) }));

	const addStep = (type) => setForm((prev) => ({ ...prev, steps: [...prev.steps, STEP_FACTORIES[type]()] }));
	const updateStep = (index, next) => setForm((prev) => ({ ...prev, steps: prev.steps.map((s, i) => (i === index ? next : s)) }));

	// Deleting a step shifts every later step's position down by one, so any
	// "then go to" reference pointing past it needs to shift too -- and any
	// reference pointing AT the deleted step is now dangling and must be
	// cleared (rather than silently landing on whatever step slides into that
	// position next), so it shows as unset and the user re-picks it.
	const removeStep = (index) =>
		setForm((prev) => {
			const removedPosition = index + 1;
			const remapRef = (ref) => {
				if (ref === '' || ref == null) return ref;
				const n = Number(ref);
				if (!Number.isFinite(n)) return ref;
				if (n === removedPosition) return '';
				return n > removedPosition ? String(n - 1) : ref;
			};
			const remaining = prev.steps
				.filter((_, i) => i !== index)
				.map((s) => ({
					...s,
					...('next' in s ? { next: remapRef(s.next) } : {}),
					...('onApprove' in s ? { onApprove: remapRef(s.onApprove) } : {}),
					...('onReject' in s ? { onReject: remapRef(s.onReject) } : {}),
					...('trueNext' in s ? { trueNext: remapRef(s.trueNext) } : {}),
				}));
			return { ...prev, steps: remaining };
		});

	const buildConditions = () => {
		if (!form.leaves.length) return undefined;
		const leaves = form.leaves.filter((l) => l.field).map((l) => ({ field: l.field, operator: l.operator, value: coerceValue(l.value) }));
		if (!leaves.length) return undefined;
		return { operator: form.combinator, conditions: leaves };
	};

	const handleSave = async () => {
		if (!form.steps.length) {
			toast.error('Add at least one step.');
			return;
		}
		const payload = {
			name: form.name.trim(),
			description: form.description,
			triggerEventType: form.triggerEventType,
			conditions: buildConditions(),
			steps: toBackendSteps(form.steps),
			storeId: activeStoreId,
		};

		setSaving(true);
		const result = isNew ? await createWorkflow(payload) : await updateWorkflow(id, payload);
		setSaving(false);

		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to save workflow.');
			return;
		}
		toast.success('Saved.');
		if (isNew) {
			navigate(`/workflows/${result.data.workflow_definition_id}`, { replace: true });
		}
	};

	const handleNewDraft = async () => {
		const result = await createWorkflowDraft(id, activeStoreId);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to create a new draft.');
			return;
		}
		toast.success('New draft created.');
		navigate(0);
	};

	const handlePublish = async () => {
		setSaving(true);
		const result = await publishWorkflow(id, activeStoreId);
		setSaving(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to publish workflow.');
			return;
		}
		toast.success('Workflow published.');
		navigate(0);
	};

	if (loading) return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;

	const editingPublished = meta && meta.editable_version?.status === 'PUBLISHED';

	return (
		<div className='py-8 pr-8 max-w-3xl'>
			<div className='mb-6'>
				<Link to='/workflows' className='text-sm text-gray-500 hover:underline'>
					&larr; All Workflows
				</Link>
				<h1 className='text-2xl font-light tracking-tight mt-1'>{isNew ? 'Create Workflow' : 'Edit Workflow'}</h1>
				{meta && <p className='text-sm text-gray-500'>Status: {meta.status} · Version {meta.editable_version?.version}</p>}
			</div>

			{editingPublished && (
				<div className='mb-6 border border-amber-200 bg-amber-50 rounded-lg p-4 flex items-center justify-between'>
					<p className='text-sm text-amber-800'>The published version can't be edited. Create a new draft to make changes.</p>
					<button type='button' className='btn-outline-small' onClick={handleNewDraft}>
						Create Draft
					</button>
				</div>
			)}

			<div className='space-y-8'>
				<div>
					<label>Name</label>
					<input className='form-input' value={form.name} onChange={(e) => setField('name', e.target.value)} disabled={editingPublished} />
				</div>
				<div>
					<label>Description</label>
					<textarea className='form-input' rows={4} value={form.description} onChange={(e) => setField('description', e.target.value)} disabled={editingPublished} />
				</div>

				<div>
					<h2 className='font-medium mb-3 inline-flex items-center gap-1.5'>
						When this happens
						<HelpModalButton title='Available triggers'>
							<TriggerReferenceContent />
						</HelpModalButton>
					</h2>
					<select className='form-input max-w-sm' value={form.triggerEventType} onChange={(e) => setField('triggerEventType', e.target.value)} disabled={editingPublished}>
						{TRIGGER_OPTIONS.map((t) => (
							<option key={t} value={t}>
								{humanizeEventType(t)}
							</option>
						))}
					</select>
				</div>

				<div>
					<div className='flex items-center justify-between mb-3'>
						<h2 className='font-medium inline-flex items-center gap-1.5'>
							Only if
							<HelpModalButton title='Available fields & operators'>
								<FieldReferenceContent />
							</HelpModalButton>
						</h2>
						{form.leaves.length > 1 && (
							<select className='form-input w-32' value={form.combinator} onChange={(e) => setField('combinator', e.target.value)} disabled={editingPublished}>
								<option value='AND'>ALL true</option>
								<option value='OR'>ANY true</option>
							</select>
						)}
					</div>
					<div className='space-y-2'>
						{form.leaves.map((leaf, index) => (
							<div key={index} className='grid grid-cols-[1fr_180px_1fr_auto] gap-2 items-center'>
								<input className='form-input' placeholder='order.total' value={leaf.field} onChange={(e) => updateLeaf(index, { field: e.target.value })} disabled={editingPublished} />
								<select className='form-input' value={leaf.operator} onChange={(e) => updateLeaf(index, { operator: e.target.value })} disabled={editingPublished}>
									{OPERATORS.map((op) => (
										<option key={op} value={op}>
											{op}
										</option>
									))}
								</select>
								<input className='form-input' placeholder='5000' value={leaf.value} onChange={(e) => updateLeaf(index, { value: e.target.value })} disabled={editingPublished} />
								{!editingPublished && (
									<button type='button' className='text-gray-400 hover:text-red-600' onClick={() => removeLeaf(index)}>
										<Trash2 className='size-4' />
									</button>
								)}
							</div>
						))}
					</div>
					{!editingPublished && (
						<button type='button' className='btn-secondary-small mt-2 inline-flex items-center gap-1' onClick={addLeaf}>
							<Plus className='size-3.5' /> Add condition
						</button>
					)}
					{!form.leaves.length && <p className='text-xs text-gray-400 mt-1'>No conditions - this workflow runs on every matching event.</p>}
				</div>

				<div>
					<h2 className='font-medium mb-3 inline-flex items-center gap-1.5'>
						Then
						<HelpModalButton title='Available actions & template variables'>
							<ActionReferenceContent />
						</HelpModalButton>
					</h2>
					<div className='space-y-3'>
						{form.steps.map((step, index) => (
							<StepCard key={index} step={step} index={index} steps={form.steps} onChange={(next) => updateStep(index, next)} onRemove={() => removeStep(index)} />
						))}
					</div>
					{!editingPublished && (
						<div className='flex gap-2 mt-3'>
							{Object.keys(STEP_FACTORIES).map((type) => (
								<button key={type} type='button' className='btn-secondary-small inline-flex items-center gap-1' onClick={() => addStep(type)}>
									<Plus className='size-3.5' /> {type}
								</button>
							))}
						</div>
					)}
				</div>

				<div className='flex justify-end gap-3 pt-2'>
					<Link to='/workflows' className='btn-secondary pt-1'>
						Cancel
					</Link>
					{!editingPublished && (
						<button type='button' disabled={saving} className='btn-outline' onClick={handleSave}>
							{saving ? 'Saving...' : 'Save Draft'}
						</button>
					)}
					{!isNew && !editingPublished && (
						<button type='button' disabled={saving} className='btn-primary' onClick={handlePublish}>
							{saving ? 'Publishing...' : 'Publish'}
						</button>
					)}
				</div>
			</div>
		</div>
	);
}
